import React, { PropsWithChildren, useEffect, useMemo } from 'react';

import { getAdminApiUrl } from '@adsk/acs-sc-web-platform-api';
import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useStoreWithValidation,
} from '@adsk/alloy-react-context';

import { useCurrentAccountId } from './CurrentAccountIdContext';
import { useProviderOnlyOnce } from './hooks/useProviderOnlyOnce';
import {
  type AccountOfferingsStore,
  useAccountOfferingsStore,
} from './stores/AccountOfferingsStore';

export type {
  AccountOffering,
  AccountOfferingsResponse,
} from './stores/AccountOfferingsStore';

type AccountOfferingsContextValue = {
  data: AccountOfferingsStore['data'];
  loading: boolean;
  error: AccountOfferingsStore['error'];
  retry: () => void;
};

export const AccountOfferingsContext =
  getOrCreateGlobalContext<AccountOfferingsContextValue>(
    CONTEXT_NAMES.AccountOfferingsContext,
  );

export const useAccountOfferings = (disabled?: boolean) => {
  const { data, error, loading, fetchData, retry } =
    useStoreWithValidation<AccountOfferingsStore>(
      CONTEXT_NAMES.AccountOfferingsContext,
      {
        hook: 'useAccountOfferings',
        provider: 'AccountOfferingsProvider',
      },
    );

  const accountId = useCurrentAccountId();

  const url = useMemo(() => {
    if (!accountId) {
      return null;
    }
    return `${getAdminApiUrl()}/v1/accounts/${accountId}/offerings?filter[type]=-none&filter[includeTrials]=true`;
  }, [accountId]);

  useEffect(() => {
    if (!disabled && url) {
      fetchData(url);
    }
  }, [disabled, url, fetchData]);

  return {
    data,
    loading: disabled ? false : loading,
    error,
    retry,
  };
};

export const useAccountOfferingsProvider = () => {
  const { data, error, loading, retry } = useAccountOfferingsStore();

  return useMemo(
    () => ({
      data,
      loading,
      error,
      retry,
    }),
    [data, loading, error, retry],
  );
};

export function AccountOfferingsProvider({
  children,
}: Readonly<PropsWithChildren>) {
  const accountOfferingsContextValue = useAccountOfferingsProvider();

  useProviderOnlyOnce({
    context: AccountOfferingsContext,
    contextName: CONTEXT_NAMES.AccountOfferingsContext,
  });

  return (
    <AccountOfferingsContext.Provider value={accountOfferingsContextValue}>
      {children}
    </AccountOfferingsContext.Provider>
  );
}

AccountOfferingsProvider.displayName = 'AccountOfferingsProvider';
